import type { LucideIcon } from "lucide-react";
import type { Variants } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared surface + motion language for the app shells, pulled straight from the
 * landing's "lime planner" look: chunky rounded cards, a dark ink hero with a
 * lime spark chip, concentric-ring texture, and a lime glow. Everything reads
 * the semantic theme tokens so it flips cleanly between light and dark.
 */
export const EASE = [0.22, 0.8, 0.35, 1] as const;

export const containerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

/** Hero banner entrance: a chunky fade + rise, independent of body stagger. */
export const headerVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

/** Token-driven hover gradient, kept for packs still wiring <MagicCard>. */
export const cardGradient = {
  gradientSize: 260,
  gradientFrom: "var(--primary)",
  gradientTo: "var(--secondary)",
  gradientColor: "color-mix(in oklch, var(--primary) 18%, transparent)",
} as const;

/**
 * Standard body card. Chunky 22px radius, a solid card fill, and a soft lift
 * shadow so it reads like the landing's raised planner tiles instead of a
 * hairline SaaS box. The lift is ink-tinted (`--foreground`) in light mode; in
 * dark mode foreground is near-white and would glow, so the dark variant drops
 * to a tighter `--background`-tinted shadow, same trick as the hero banner.
 */
export const surface = cn(
  "rounded-[calc(var(--radius)+6px)] border border-border/60 bg-card",
  "shadow-[0_16px_38px_-26px_color-mix(in_oklch,var(--foreground)_45%,transparent)]",
  "dark:shadow-[0_10px_24px_-18px_color-mix(in_oklch,var(--background)_90%,transparent)]",
);

/** The landing's four-point spark. Reused as the default hero mark. */
export function SparkMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M12 2l2.4 6.6 6.6 2.4-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" />
    </svg>
  );
}

/**
 * Lime chip that anchors the hero. Shows the page's icon, an avatar/logo, or the
 * spark mark as a fallback, always on the bright brand lime.
 */
export function HeroChip({ icon: Icon, iconUrl }: { icon?: LucideIcon; iconUrl?: string | null }) {
  if (iconUrl) {
    return (
      <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-full ring-2 ring-foreground/15">
        <img src={iconUrl} alt="" className="size-full object-cover" />
      </span>
    );
  }
  return (
    <span className="grid size-12 shrink-0 place-items-center rounded-full bg-primary text-[var(--primary-foreground)] shadow-[0_10px_24px_-12px_color-mix(in_oklch,var(--primary)_80%,transparent)]">
      {Icon ? <Icon className="size-6" /> : <SparkMark className="size-[22px] fill-[var(--primary-foreground)]" />}
    </span>
  );
}

/** Uppercase pill above the hero title (status, date, tag). */
export function HeroBadge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-2 rounded-full bg-foreground/[0.06] px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-foreground/80 ring-1 ring-foreground/15",
        "[&>svg]:size-3.5 [&>svg]:shrink-0",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Hero backdrop: concentric lime rings in the top-right, a warm lime glow in
 * the bottom-right, and a lime top hairline. Same motifs as the landing CTA and
 * the "Why teams switch" bento card, all from theme tokens.
 */
export function HeroBackdrop() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[130px] -top-[170px] size-[440px] rounded-full [background:repeating-radial-gradient(circle_at_center,transparent_0_28px,color-mix(in_oklch,var(--primary)_12%,transparent)_28px_29px)]"
        style={{
          maskImage: "radial-gradient(60% 60% at 50% 50%, #000 40%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(60% 60% at 50% 50%, #000 40%, transparent 78%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(420px 300px at 96% 108%, color-mix(in oklch, var(--primary) 30%, transparent) 0%, transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, color-mix(in oklch, var(--primary) 75%, transparent), transparent)",
        }}
      />
    </>
  );
}

/**
 * Shared hero wrapper used by both page shells: an elevated card surface that follows the theme
 * (light card with dark text in light mode, dark card with light text in dark mode), with the
 * lime rings and glow on top. It used to stay a dark ink block in light mode too; the user wanted
 * the headers light there.
 *
 * `overflow-clip`, not `overflow-hidden`: the ring texture hangs past the right edge, and a hidden
 * overflow is still a scroll container, so focusing a header action (opening a menu, closing a
 * dialog) scrolled the whole banner sideways and cut off its left side.
 */
export function heroBannerClassName(className?: string) {
  return cn(
    "relative overflow-clip rounded-[calc(var(--radius)+10px)] border border-border/70 bg-card text-foreground",
    "shadow-[0_24px_60px_-40px_color-mix(in_oklch,var(--foreground)_35%,transparent)]",
    "dark:shadow-[0_24px_60px_-40px_color-mix(in_oklch,var(--background)_85%,transparent)]",
    className,
  );
}

/**
 * Trailing hero actions (Manage Billing, Read the docs, etc.), drawn as translucent pill chips
 * that read on the hero card in both themes.
 */
export const heroActionsClassName = cn(
  "flex shrink-0 flex-wrap items-center gap-2",
  "[&_a]:rounded-full [&_button]:rounded-full",
  "[&_a]:border-transparent [&_button]:border-transparent",
  "[&_a]:font-bold [&_button]:font-bold",
  "[&_a]:bg-foreground/[0.06] [&_button]:bg-foreground/[0.06]",
  "[&_a]:text-foreground [&_button]:text-foreground",
  "[&_a]:ring-1 [&_button]:ring-1",
  "[&_a]:ring-foreground/15 [&_button]:ring-foreground/15",
  "[&_a:hover]:bg-foreground/10 [&_button:hover]:bg-foreground/10",
  // The page's one main action keeps its lime fill: `data-hero-primary` outranks the pill chips.
  "[&_[data-hero-primary]]:bg-primary [&_[data-hero-primary]]:text-primary-foreground [&_[data-hero-primary]]:ring-0",
  "[&_[data-hero-primary]:hover]:bg-primary/90",
);
